import { IgSelect } from "@intelligems/ig-theme/IgSelect";
import { IgToggleButtonGroup } from "@intelligems/ig-theme/IgToggleButtonGroup";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import type { ComponentPlacementLocation } from "@intelligems/ig-types/dtos/components";
import type { InsertType } from "@intelligems/ig-types/dtos/onsite-edits";
import { MenuItem, ToggleButton } from "@mui/material";
import Stack from "@mui/material/Stack";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import {
  INSERT_TYPE_LABELS,
  PLACEMENT_LOCATION_LABELS,
  PLACEMENT_MODE_LABELS,
} from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/options";
import type { PlacementMode } from "@src/store/slices/experienceBuilder/types";

const modeButtons = (Object.keys(PLACEMENT_MODE_LABELS) as PlacementMode[]).map(
  (mode) => (
    <ToggleButton value={mode} key={mode} size="small" sx={{ flex: 1 }}>
      {PLACEMENT_MODE_LABELS[mode]}
    </ToggleButton>
  )
);

// Placement: which surface (pdp | cart), then one of two ways to pick the spot.
//
// - Theme block: the merchant drops Intelligems Content blocks wherever
//   they want in the theme editor and gives them a key. Nothing here can list
//   those blocks — a theme app extension's settings are static Liquid — so the
//   key is typed on both sides and matching the strings is the binding.
// - Anchor selector: the legacy mode, a CSS selector on the live theme.
//
// Location drives the whole rule surface, so switching it is handled by the
// container (resets selector + prunes conditions).
export const PlacementSection = ({
  location,
  mode,
  anchorKey,
  selector,
  insertType,
  anchorKeyError,
  selectorError,
  onLocationChange,
  onModeChange,
  onChange,
}: {
  location: ComponentPlacementLocation;
  mode: PlacementMode;
  anchorKey: string;
  selector: string;
  insertType: InsertType | undefined;
  anchorKeyError?: boolean;
  selectorError?: boolean;
  onLocationChange: (location: ComponentPlacementLocation) => void;
  onModeChange: (mode: PlacementMode) => void;
  onChange: (changes: {
    anchorKey?: string;
    selector?: string;
    insertType?: InsertType;
  }) => void;
}) => (
  <Stack spacing={5}>
    <Panel.Header>Placement</Panel.Header>
    <IgSelect<ComponentPlacementLocation>
      fullWidth
      size="small"
      value={location}
      onChange={(e) => onLocationChange(e.target.value)}
    >
      {(
        Object.keys(PLACEMENT_LOCATION_LABELS) as ComponentPlacementLocation[]
      ).map((loc) => (
        <MenuItem key={loc} value={loc}>
          {PLACEMENT_LOCATION_LABELS[loc]}
        </MenuItem>
      ))}
    </IgSelect>

    <IgToggleButtonGroup
      buttons={modeButtons}
      value={mode}
      sx={{ width: "100%", flexShrink: 0 }}
      control={{
        exclusive: true,
        onChange: (_event, value: PlacementMode) => {
          if (value) {
            onModeChange(value);
          }
        },
      }}
    />

    {mode === "themeBlock" ? (
      <Stack spacing={1}>
        <Panel.Label>Anchor key</Panel.Label>
        <Panel.TextField
          value={anchorKey}
          onChange={(e) => onChange({ anchorKey: e.target.value })}
          placeholder="product-recommendations"
          error={anchorKeyError}
        />
        <IgTypography variant="body2" color="text.secondary">
          In your theme editor, add the Intelligems Content block where you want
          these products, and enter this same key in its settings. Add one block
          per spot — several blocks can share this key.
        </IgTypography>
      </Stack>
    ) : (
      <>
        <Stack spacing={1}>
          <Panel.Label>Anchor selector</Panel.Label>
          <Panel.TextField
            value={selector}
            onChange={(e) => onChange({ selector: e.target.value })}
            placeholder='form[action*="/cart/add"]:has([type="submit"])'
            error={selectorError}
          />
        </Stack>

        <IgSelect<InsertType>
          label="Position"
          fullWidth
          size="small"
          value={insertType ?? "afterend"}
          onChange={(e) => onChange({ insertType: e.target.value })}
        >
          {(Object.keys(INSERT_TYPE_LABELS) as InsertType[]).map((it) => (
            <MenuItem key={it} value={it}>
              {INSERT_TYPE_LABELS[it]}
            </MenuItem>
          ))}
        </IgSelect>
      </>
    )}
  </Stack>
);
